import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CardContent,
  Input,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Snackbar,
  CssBaseline,
} from '@mui/joy';
import {
  Paper,
  Container,
  Typography as MuiTypography,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  WhatsApp as WhatsAppIcon,
  LocalShipping as ShippingIcon,
  VerifiedUser as AuthIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { 
  getAllSettings,
  updateSettingById,
} from '../../main/API/settings';
import PermissionGate from '../../main/components/PermissionGate';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { translateAxiosErrorMessage } from '../../main/utils/masterApiError';
import {
  ParamsTable,
  OTP_PARAMS,
  INVOICE_PARAMS,
  SHIPMENT_STATUS_PARAMS,
  SHIPMENT_DELETED_PARAMS,
} from '../constants/contentSidParams';
import WhatsappNotifyFlagsSetting from './widgets/WhatsappNotifyFlagsSetting';

export default function WhatsAppMessagesScreen() {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [allSettings, setAllSettings] = useState([]);
  const [editingValues, setEditingValues] = useState({});
  const [savingSections, setSavingSections] = useState({});
  const [initialLoad, setInitialLoad] = useState(false);
  
  // Notification states
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");

  const loadAllSettings = useCallback(async () => {
    console.log('WhatsAppMessagesScreen: loadAllSettings called');
    if (!hasPermission(PERMISSIONS.SETTINGS_LIST)) {
      console.warn('User does not have permission to access settings');
      setAllSettings([]);
      return;
    }

    setSettingsLoading(true);
    try {
      const settings = await getAllSettings();
      setAllSettings(Array.isArray(settings) ? settings : []);
    } catch (error) {
      console.error('Failed to load settings:', error);
      setResponseMessage(t('settingsPages.common.loadFailed'));
      setFailedNotification(true);
      setAllSettings([]);
    } finally {
      setSettingsLoading(false);
    }
  }, [hasPermission, t]);

  useEffect(() => {
    if (!initialLoad) {
      console.log('WhatsAppMessagesScreen: Initial load starting');
      setInitialLoad(true);
      loadAllSettings();
    }
  }, [initialLoad, loadAllSettings]);

  // Helper function to format setting names for better readability
  const formatSettingName = (name) => {
    if (!name) return '';
    
    const acronyms = ['API', 'SID', 'URL', 'HMAC', 'SMS', 'ID', 'UID', 'UUID'];
    
    const parts = name.split('_').map(part => {
      const upperPart = part.toUpperCase();
      
      if (acronyms.includes(upperPart)) {
        return upperPart;
      }
      
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    });
    
    return parts.join(' ');
  };

  // Section definitions: order of names determines display order within section.
  const SECTIONS = useMemo(
    () => [
      {
        id: 'shipment',
        title: t('settingsPages.whatsAppMessages.sectionShipment'),
        description: t('settingsPages.whatsAppMessages.sectionShipmentDesc'),
        icon: ShippingIcon,
        color: '#0EA5E9',
        bgColor: '#E0F2FE',
        settingNames: [
          'SHIPMENT_SHIPPING_SOON_CONTENT_SID',
          'SHIPMENT_SHIPPED_CONTENT_SID',
          'SHIPMENT_ARRIVED_CONTENT_SID',
          'SHIPMENT_OUT_FOR_DELIVERY_CONTENT_SID',
          'SHIPMENT_DELIVERED_CONTENT_SID',
          'SHIPMENT_DELETED_CONTENT_SID',
          'STORE_CONTENT_SID',
        ],
      },
      {
        id: 'auth-docs',
        title: t('settingsPages.whatsAppMessages.sectionAuth'),
        description: t('settingsPages.whatsAppMessages.sectionAuthDesc'),
        icon: AuthIcon,
        color: '#8B5CF6',
        bgColor: '#EDE9FE',
        settingNames: ['OTP_CONTENT_SID', 'INVOICE_CONTENT_SID'],
      },
    ],
    [t],
  );

  const allWhatsAppSettingNames = SECTIONS.flatMap(s => s.settingNames);
  const getWhatsAppMessagesSettings = () =>
    allSettings.filter(setting => allWhatsAppSettingNames.includes(setting.name));

  // Group settings by section for layout
  const getSettingsBySection = () => {
    const flat = getWhatsAppMessagesSettings();
    return SECTIONS.map(section => ({
      ...section,
      settings: section.settingNames
        .map(name => flat.find(s => s.name === name))
        .filter(Boolean),
    })).filter(section => section.settings.length > 0);
  };

  // Handle setting value change
  const handleSettingValueChange = (settingId, value) => {
    setEditingValues(prev => ({
      ...prev,
      [settingId]: value
    }));
  };

  // Handle save all settings
  const handleSaveSection = async (settings) => {
    if (!hasPermission(PERMISSIONS.SETTINGS_UPDATE_DETAIL)) {
      setResponseMessage(t('permissions.noPermission', { defaultValue: 'You do not have permission to perform this action.' }));
      setFailedNotification(true);
      return Promise.resolve();
    }
    const sectionKey = 'WhatsAppMessages-main';
    
    const hasChanges = settings.some(setting => {
      const editedValue = editingValues[setting.id];
      return editedValue !== undefined && editedValue !== setting.value;
    });

    if (!hasChanges) {
      setResponseMessage(t('settingsPages.common.noChangesToSave'));
      setFailedNotification(true);
      return Promise.resolve();
    }

    setSavingSections(prev => ({ ...prev, [sectionKey]: true }));
    
    try {
      const savePromises = settings.map(async (setting) => {
        const newValue = editingValues[setting.id] !== undefined 
          ? editingValues[setting.id] 
          : setting.value;

        if (newValue !== setting.value) {
          const settingData = {
            name: setting.name,
            value: newValue,
            description: setting.description || ''
          };
          return updateSettingById(setting.id, settingData);
        }
        return Promise.resolve();
      });

      await Promise.all(savePromises);
      
      setResponseMessage(t('settingsPages.whatsAppMessages.updatedSuccess'));
      setSuccessNotification(true);
      
      const updatedValuesMap = {};
      settings.forEach(setting => {
        const editedValue = editingValues[setting.id];
        if (editedValue !== undefined) {
          updatedValuesMap[setting.id] = editedValue;
        }
      });
      
      setAllSettings(prevSettings => {
        return prevSettings.map(setting => {
          if (setting.id in updatedValuesMap) {
            return {
              ...setting,
              value: updatedValuesMap[setting.id]
            };
          }
          return setting;
        });
      });
      
      setEditingValues(prev => {
        const updated = { ...prev };
        settings.forEach(setting => {
          delete updated[setting.id];
        });
        return updated;
      });
      
      return Promise.resolve();
    } catch (error) {
      console.error('Failed to save settings:', error);
      const errorMessage = translateAxiosErrorMessage(error, t) || t('settingsPages.common.saveFailed');
      setResponseMessage(errorMessage);
      setFailedNotification(true);
      return Promise.reject(error);
    } finally {
      setSavingSections(prev => {
        const updated = { ...prev };
        delete updated[sectionKey];
        return updated;
      });
    }
  };

  const whatsappSettings = getWhatsAppMessagesSettings();
  const settingsBySection = getSettingsBySection();
  const sectionKey = 'WhatsAppMessages-main';
  const isSaving = savingSections[sectionKey] || false;
  const hasChanges = whatsappSettings.some(setting => {
    const editedValue = editingValues[setting.id];
    return editedValue !== undefined && editedValue !== setting.value;
  });

  const CONTENT_SID_HELP = useMemo(() => ({
    OTP_CONTENT_SID: <ParamsTable params={OTP_PARAMS} subtitle={t('settingsPages.common.templateParams', { params: '{{1}}' })} />,
    INVOICE_CONTENT_SID: <ParamsTable params={INVOICE_PARAMS} subtitle={t('settingsPages.twilio.helpInvoice')} maxHeight={380} />,
    SHIPMENT_SHIPPING_SOON_CONTENT_SID: <ParamsTable params={SHIPMENT_STATUS_PARAMS} subtitle={t('settingsPages.common.templateParams', { params: '{{1}}–{{29}}' })} />,
    SHIPMENT_SHIPPED_CONTENT_SID: <ParamsTable params={SHIPMENT_STATUS_PARAMS} subtitle={t('settingsPages.common.templateParams', { params: '{{1}}–{{29}}' })} />,
    SHIPMENT_ARRIVED_CONTENT_SID: <ParamsTable params={SHIPMENT_STATUS_PARAMS} subtitle={t('settingsPages.common.templateParams', { params: '{{1}}–{{29}}' })} />,
    SHIPMENT_OUT_FOR_DELIVERY_CONTENT_SID: <ParamsTable params={SHIPMENT_STATUS_PARAMS} subtitle={t('settingsPages.common.templateParams', { params: '{{1}}–{{29}}' })} />,
    SHIPMENT_DELIVERED_CONTENT_SID: <ParamsTable params={SHIPMENT_STATUS_PARAMS} subtitle={t('settingsPages.common.templateParams', { params: '{{1}}–{{29}}' })} />,
    SHIPMENT_DELETED_CONTENT_SID: <ParamsTable params={SHIPMENT_DELETED_PARAMS} subtitle={t('settingsPages.common.templateParams', { params: '{{1}}–{{28}}' })} maxHeight={380} />,
    STORE_CONTENT_SID: (
      <span style={{ fontSize: '0.8125rem', display: 'block' }}>
        {t('settingsPages.whatsAppMessages.helpStoreContentSid')}
        <br />
        {t('settingsPages.twilio.helpNoParams')}
      </span>
    ),
  }), [t]);

  const renderSettingField = (setting) => {
    const currentValue = editingValues[setting.id] !== undefined
      ? editingValues[setting.id]
      : (setting.value || '');
    const helpContent = CONTENT_SID_HELP[setting.name];
    const label = (
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
        {formatSettingName(setting.name)}
        {helpContent && (
          <Tooltip
            title={helpContent}
            placement="top"
            arrow
            componentsProps={{ tooltip: { sx: { maxWidth: 440, maxHeight: 380 } } }}
          >
            <InfoIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle', cursor: 'help', color: '#6B7280' }} />
          </Tooltip>
        )}
      </Typography>
    );
    return (
      <Grid item xs={12} md={6} key={setting.id}>
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid #F3F4F6',
            bgcolor: '#FFFFFF',
            height: '100%',
            transition: 'all 0.2s ease',
            '&:hover': { border: '1px solid #E5E7EB', boxShadow: 1 },
          }}
        >
          {label}
          <Input
            fullWidth
            size="sm"
            value={currentValue}
            onChange={(e) => handleSettingValueChange(setting.id, e.target.value)}
            placeholder={t('settingsPages.common.contentSidPlaceholder')}
            disabled={isSaving}
            sx={{ '& input': { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
          />
        </Box>
      </Grid>
    );
  };

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFA" }}>
        <Box component="main" sx={{ overflow: "auto", pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
              <MuiTypography variant="h4" sx={{ fontWeight: 700, color: "#1F2937", mb: 1 }}>
                {t('settingsPages.whatsAppMessages.pageTitle')}
              </MuiTypography>
              <MuiTypography variant="body1" color="#6B7280" sx={{ fontSize: '1.1rem' }}>
                {t('settingsPages.whatsAppMessages.pageSubtitle')}
              </MuiTypography>
            </Box>

            {/* Auto-notification flags live above the Content SID editors:
                operators usually pick which milestones notify before fine-
                tuning the templates. The widget loads its own copy of the
                settings via `getAllSettings` so it stays independent of this
                page's editing buffer. */}
            <WhatsappNotifyFlagsSetting />

            <PermissionGate permissions={[PERMISSIONS.SETTINGS_LIST]}>
              <Paper 
                elevation={0} 
                sx={{ 
                  mb: 4,
                  borderRadius: 3,
                  border: '1px solid #E5E7EB',
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: '#D1FAE5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.5
                        }}
                      >
                        <WhatsAppIcon sx={{ color: '#059669', fontSize: 24 }} />
                      </Box>
                      <MuiTypography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
                        {t('settingsPages.whatsAppMessages.cardTitle')}
                      </MuiTypography>
                    </Box>
                    <Divider />
                  </Box>

                  {settingsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : whatsappSettings.length === 0 ? (
                    <Alert severity="info">
                      {t('settingsPages.whatsAppMessages.empty')}
                    </Alert>
                  ) : (
                    <Box>
                      {settingsBySection.map((section) => {
                        const SectionIcon = section.icon;
                        return (
                          <Box key={section.id} sx={{ mb: 4 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                mb: 1.5,
                                pb: 1,
                                borderBottom: '2px solid',
                                borderColor: section.color,
                              }}
                            >
                              <Box
                                sx={{
                                  p: 1,
                                  borderRadius: 1.5,
                                  bgcolor: section.bgColor,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <SectionIcon sx={{ color: section.color, fontSize: 22 }} />
                              </Box>
                              <Box>
                                <MuiTypography variant="subtitle1" sx={{ fontWeight: 700, color: '#1F2937' }}>
                                  {section.title}
                                </MuiTypography>
                                <MuiTypography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                                  {section.description}
                                </MuiTypography>
                              </Box>
                            </Box>
                            <Grid container spacing={2}>
                              {section.settings.map(renderSettingField)}
                            </Grid>
                          </Box>
                        );
                      })}

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, borderTop: '1px solid #E5E7EB' }}>
                        <PermissionGate permissions={[PERMISSIONS.SETTINGS_MANAGE]}>
                          <Button
                            type="button"
                            variant="contained"
                            size="sm"
                            startDecorator={isSaving ? <CircularProgress sx={{ width: 16, height: 16 }} /> : <SaveIcon />}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
                              handleSaveSection(whatsappSettings).then(() => {
                                requestAnimationFrame(() => {
                                  window.scrollTo(0, scrollPosition);
                                });
                              });
                            }}
                            disabled={isSaving || !hasChanges}
                            sx={{ minWidth: 120 }}
                          >
                            {isSaving ? t('settingsPages.common.saving') : t('settingsPages.common.saveAllChanges')}
                          </Button>
                        </PermissionGate>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Paper>
            </PermissionGate>

            {/* Success Notification */}
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={(event, reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                setSuccessNotification(false);
              }}
            >
              {responseMessage}
            </Snackbar>

            {/* Error Notification */}
            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={(event, reason) => {
                if (reason === 'clickaway') {
                  return;
                }
                setFailedNotification(false);
              }}
            >
              {responseMessage}
            </Snackbar>
          </Container>
        </Box>
      </Box>
    </DirectionalMaterialThemeProvider>
  );
}

